import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import HabboImage from '../../../components/HabboImage';
import { getBadgeUrl } from '../../../utils/badgeUrl';
import { searchLookupUsers, getLookupUserOverview } from '../../../services/api';

const GAME_DISPLAY_NAMES = {
    lobbyburst: 'Lobby Burst',
};

const ENGAGEMENT_DISPLAY_NAMES = {
    like: 'Likes',
    luv: 'Luv',
};

const EXACT_PIXEL_IMAGE = {
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'none',
    flexShrink: 0,
};

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded text-sm';
const smallButtonShadow = { boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' };

const getGameDisplayName = (identifier) =>
    GAME_DISPLAY_NAMES[identifier.toLowerCase()]
    || identifier.charAt(0).toUpperCase() + identifier.slice(1);

const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return Number(value).toLocaleString('en-GB');
};

const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatXp = (value) => {
    if (!value) return '-';
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}m`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
    return String(value);
};

const daysSince = (value) => {
    if (!value) return '-';
    return Math.floor((Date.now() - new Date(value).getTime()) / 86_400_000).toLocaleString('en-GB');
};

const winRate = (won, played) => {
    if (!played) return '-';
    return `${Math.round((won / played) * 100)}%`;
};

function StatTile({ label, value, sub }) {
    return (
        <div
            className="bg-white border border-gray-200 p-3 flex flex-col gap-1"
            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.08)' }}
        >
            <span className="font-['Jersey_10'] text-[26px] leading-none text-gray-900 tracking-widest">
                {value}
            </span>
            <span className="font-['Jersey_10'] text-[14px] uppercase tracking-widest text-gray-500 leading-tight">
                {label}
            </span>
            {sub && (
                <span className="font-['Jersey_10'] text-[13px] text-gray-400 leading-none mt-0.5 tracking-widest">
                    {sub}
                </span>
            )}
        </div>
    );
}

function StatCategory({ header, tint, children }) {
    return (
        <ContentBox theme="default" tint={tint} header={header}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {children}
            </div>
        </ContentBox>
    );
}

function UserLookup({ userData }) {
    const [term, setTerm] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    const searchToken = useRef(0);

    useEffect(() => {
        const trimmed = term.trim();

        if (trimmed.length < 2) {
            setResults([]);
            setSearching(false);
            return;
        }

        setSearching(true);
        const token = ++searchToken.current;

        const timer = setTimeout(async () => {
            try {
                const response = await searchLookupUsers(trimmed);
                if (token !== searchToken.current) return;
                setResults(response.success ? response.users : []);
            } catch (error) {
                if (token !== searchToken.current) return;
                console.error('Failed to search users:', error);
                setResults([]);
            } finally {
                if (token === searchToken.current) setSearching(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [term]);

    const loadUser = useCallback(async (identifier) => {
        setLoading(true);
        try {
            const response = await getLookupUserOverview(identifier);
            if (response.success) {
                setStats(response.stats);
                setResults([]);
            } else {
                toast.error(response.message || 'Failed to load this user');
            }
        } catch (error) {
            console.error('Failed to load user overview:', error);
            toast.error(error?.status === 404 ? 'User not found' : 'Failed to load this user');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = term.trim();
        if (!trimmed) return;
        loadUser(results[0]?.userId || trimmed);
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const totalComments = stats
        ? stats.community.profileComments + stats.community.articleComments
        : 0;

    return (
        <AdminLayout>
            <ContentBox theme="default" tint="red" header="User Lookup">
                <form onSubmit={handleSubmit} className="flex gap-2 mb-3">
                    <input
                        type="text"
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        placeholder="Search by Habbo name..."
                        className={inputClass}
                        autoFocus
                    />
                    <button
                        type="submit"
                        className="bg-maroon text-white px-6 py-2 text-sm uppercase tracking-wider whitespace-nowrap"
                        style={smallButtonShadow}
                    >
                        Look Up
                    </button>
                </form>

                {searching && <p className="text-sm uppercase text-gray-500">Searching...</p>}

                {!searching && term.trim().length >= 2 && results.length === 0 && (
                    <p className="text-sm uppercase text-gray-500">No users found</p>
                )}

                {results.length > 0 && (
                    <div className="space-y-2">
                        {results.map((user) => (
                            <button
                                key={user.userId}
                                onClick={() => loadUser(user.userId)}
                                className="w-full bg-gray-100 hover:bg-gray-200 p-2 rounded flex items-center gap-3 text-left transition-colors"
                            >
                                <div className="w-10 h-10 border border-black bg-gray-300 overflow-hidden flex items-center justify-center shrink-0">
                                    <HabboImage
                                        habbo={user.habboName}
                                        hotel={user.hotel || 'COM'}
                                        headonly
                                        direction={2}
                                        head_direction={2}
                                        size="s"
                                        style={EXACT_PIXEL_IMAGE}
                                        alt={user.habboName}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold">{user.habboName}</div>
                                    <div className="text-xs text-gray-600">
                                        {user.hotel || 'COM'} &middot; Joined {formatDate(user.memberSince)}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </ContentBox>

            {loading && (
                <ContentBox theme="default" tint="red" header="Loading">
                    <p className="text-sm uppercase">Loading user statistics...</p>
                </ContentBox>
            )}

            {!loading && stats && (
                <>
                    <ContentBox theme="default" tint="cornflowerblue" header={stats.user.habboName}>
                        <div className="flex items-start gap-4">
                            <div className="w-16 h-20 border border-black bg-gray-300 overflow-hidden flex items-end justify-center shrink-0">
                                <HabboImage
                                    habbo={stats.user.habboName}
                                    hotel={stats.user.hotel}
                                    direction={2}
                                    head_direction={2}
                                    size="s"
                                    style={EXACT_PIXEL_IMAGE}
                                    alt={stats.user.habboName}
                                />
                            </div>
                            <div className="flex-1 min-w-0 text-sm space-y-1">
                                <div><span className="uppercase text-gray-500">Hotel:</span> {stats.user.hotel}</div>
                                <div><span className="uppercase text-gray-500">Member since:</span> {formatDate(stats.user.memberSince)} ({daysSince(stats.user.memberSince)} days)</div>
                                <div><span className="uppercase text-gray-500">Last seen:</span> {formatDateTime(stats.user.lastSeen)}</div>
                                <div><span className="uppercase text-gray-500">Active sessions:</span> {formatNumber(stats.user.activeSessions)}</div>
                                <div className="break-all"><span className="uppercase text-gray-500">User ID:</span> {stats.user.userId}</div>
                                {stats.user.discordId && (
                                    <div className="break-all"><span className="uppercase text-gray-500">Discord ID:</span> {stats.user.discordId}</div>
                                )}
                            </div>
                        </div>
                    </ContentBox>

                    {(stats.moderation.ban || stats.moderation.restrictions) && (
                        <ContentBox theme="default" tint="red" header="Active Sanctions">
                            {stats.moderation.ban && (
                                <div className="bg-red-100 border border-red-300 p-3 rounded mb-2 text-sm">
                                    <div className="font-bold uppercase">Profile Ban</div>
                                    <div>{stats.moderation.ban.reason || 'No reason recorded'}</div>
                                    <div className="text-xs text-gray-600">
                                        Since {formatDateTime(stats.moderation.ban.createdAt)} &middot; Expires {stats.moderation.ban.expiresAt ? formatDateTime(stats.moderation.ban.expiresAt) : 'never'}
                                    </div>
                                </div>
                            )}
                            {stats.moderation.restrictions && (
                                <div className="bg-orange-100 border border-orange-300 p-3 rounded text-sm">
                                    <div className="font-bold uppercase">Restrictions</div>
                                    <div>
                                        {[
                                            stats.moderation.restrictions.posting && 'Posting',
                                            stats.moderation.restrictions.commenting && 'Commenting',
                                            stats.moderation.restrictions.liking && 'Liking',
                                            stats.moderation.restrictions.customisation && 'Customisation',
                                        ].filter(Boolean).join(', ') || 'None'}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        Expires {formatDateTime(stats.moderation.restrictions.expiresAt)}
                                    </div>
                                </div>
                            )}
                        </ContentBox>
                    )}

                    <StatCategory header="Framps &amp; Cards" tint="goldenrod">
                        <StatTile label="Framps Held" value={formatNumber(stats.stamps.currentTotal)} />
                        <StatTile label="Completed Cards" value={formatNumber(stats.stamps.completedCards)} />
                        <StatTile label="Total Framps Earned" value={formatNumber(stats.stamps.totalEarned)} />
                        <StatTile label="Framps Spent" value={formatNumber(stats.stamps.totalSpent)} />
                        <StatTile label="Framp Log Events" value={formatNumber(stats.stamps.logEvents)} />
                        <StatTile label="Last Framp Earned" value={formatDate(stats.stamps.lastEarned)} />
                    </StatCategory>

                    <StatCategory header="Community" tint="mediumseagreen">
                        <StatTile label="Friends" value={formatNumber(stats.community.friends)} sub={`${formatNumber(stats.community.topFriends)} top friends`} />
                        <StatTile label="Profile Posts" value={formatNumber(stats.community.profilePosts)} />
                        <StatTile label="Profile Comments" value={formatNumber(stats.community.profileComments)} />
                        <StatTile label="Article Comments" value={formatNumber(stats.community.articleComments)} />
                        <StatTile label="Total Comments Left" value={formatNumber(totalComments)} />
                        <StatTile label="Articles Written" value={formatNumber(stats.community.articlesAuthored)} />
                        <StatTile label="Unread Messages" value={formatNumber(stats.community.unreadMessages)} />
                        {Object.entries(stats.community.engagements || {}).map(([type, counts]) => {
                            const label = ENGAGEMENT_DISPLAY_NAMES[type]
                                || type.charAt(0).toUpperCase() + type.slice(1);
                            return (
                                <React.Fragment key={type}>
                                    <StatTile label={`${label} Received`} value={formatNumber(counts.received)} />
                                    <StatTile label={`${label} Given`} value={formatNumber(counts.given)} />
                                </React.Fragment>
                            );
                        })}
                    </StatCategory>

                    <ContentBox theme="default" tint="hotpink" header="I Appreciate You">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                            <StatTile label="Posts Sent" value={formatNumber(stats.appreciation.sent)} />
                            <StatTile label="Posts Received" value={formatNumber(stats.appreciation.received)} />
                            <StatTile label="Likes Received" value={formatNumber(stats.appreciation.likesReceived)} />
                            <StatTile label="Likes Given" value={formatNumber(stats.appreciation.likesGiven)} />
                            <StatTile label="Cards Spent" value={formatNumber(stats.appreciation.cardsSpent)} />
                            <StatTile label="Sent Anonymously" value={formatNumber(stats.appreciation.sentAnonymously)} />
                            <StatTile label="Posts Removed" value={formatNumber(stats.appreciation.removed)} />
                        </div>

                        {stats.appreciation.recent.length === 0 ? (
                            <p className="text-sm uppercase text-gray-500">No appreciation posts yet</p>
                        ) : (
                            <div className="space-y-2">
                                {stats.appreciation.recent.map((post) => (
                                    <div key={post.postId} className="bg-gray-100 p-3 rounded text-sm">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`text-xs uppercase px-2 py-0.5 rounded text-white ${post.direction === 'sent' ? 'bg-blue-600' : 'bg-green-700'}`}>
                                                {post.direction}
                                            </span>
                                            <span className="font-bold">{post.counterpart || 'Unknown'}</span>
                                            {post.status !== 'active' && (
                                                <span className="text-xs uppercase text-red-600">({post.status})</span>
                                            )}
                                            <span className="text-xs text-gray-500 ml-auto">
                                                {formatDate(post.createdAt)} &middot; {formatNumber(post.likeCount)} likes
                                            </span>
                                        </div>
                                        <div className="text-gray-700 break-words">{post.message}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ContentBox>

                    <ContentBox theme="default" tint="mediumpurple" header={`Achievements (${stats.achievements.count})`}>
                        {stats.achievements.list.length === 0 ? (
                            <p className="text-sm uppercase text-gray-500">No achievements earned yet</p>
                        ) : (
                            <div className="flex flex-wrap gap-3">
                                {stats.achievements.list.map((achievement) => (
                                    <div
                                        key={achievement.badgeID}
                                        className="flex flex-col items-center gap-1 w-[76px]"
                                        title={`${achievement.name || achievement.badgeID}${achievement.description ? `: ${achievement.description}` : ''}`}
                                    >
                                        <img
                                            src={getBadgeUrl(achievement.badgeID, achievement.uploaded)}
                                            alt={achievement.badgeID}
                                            style={{ ...EXACT_PIXEL_IMAGE, imageRendering: 'pixelated' }}
                                        />
                                        <span className="text-[10px] text-center leading-tight text-gray-600 break-words">
                                            {achievement.name || achievement.badgeID}
                                        </span>
                                        <span className="text-[10px] text-gray-400">
                                            {formatDate(achievement.dateAchieved)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ContentBox>

                    <StatCategory header="Shop &amp; Inventory" tint="darkorange">
                        <StatTile label="Completed Purchases" value={formatNumber(stats.shop.completedPurchases)} />
                        <StatTile label="Refunded Purchases" value={formatNumber(stats.shop.refundedPurchases)} />
                        <StatTile label="Framp Cards Spent" value={formatNumber(stats.shop.stampsInvested)} />
                        <StatTile label="Last Purchase" value={formatDate(stats.shop.lastPurchase)} />
                        <StatTile label="Items in Inventory" value={formatNumber(stats.shop.inventoryItems)} sub={`${formatNumber(stats.shop.inventoryRedeemed)} redeemed`} />
                        <StatTile label="Giveaway Entries" value={formatNumber(stats.shop.giveawayEntries)} sub={`${formatNumber(stats.shop.giveawayRedeemed)} redeemed`} />
                    </StatCategory>

                    {stats.games.length === 0 ? (
                        <ContentBox theme="default" tint="tomato" header="Games">
                            <p className="text-sm uppercase text-gray-500">No games played yet</p>
                        </ContentBox>
                    ) : (
                        <ContentBox theme="default" tint="tomato" header="Games">
                            <div className="flex flex-col gap-6">
                                {stats.games.map((game) => (
                                    <div key={game.identifier}>
                                        <p className="font-['Jersey_10'] uppercase text-[14px] tracking-widest text-gray-500 mb-2">
                                            {getGameDisplayName(game.identifier)}
                                        </p>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            <StatTile label="Games Played" value={formatNumber(game.played)} />
                                            <StatTile label="Games Won" value={formatNumber(game.won)} />
                                            <StatTile label="Games Lost" value={formatNumber(game.lost)} />
                                            <StatTile label="Win Rate" value={winRate(game.won, game.played)} />
                                            <StatTile label="High Score" value={formatNumber(game.highScore)} />
                                            <StatTile label="Framps Earned" value={formatNumber(game.stampsFromGames)} />
                                            <StatTile label="Last Played" value={formatDate(game.lastPlayed)} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ContentBox>
                    )}

                    <StatCategory header="Habbo Skills" tint="teal">
                        <StatTile
                            label="Fishing XP"
                            value={stats.skills.fishing ? formatXp(stats.skills.fishing.experience) : '-'}
                            sub={stats.skills.fishing ? `Last checked ${formatDate(stats.skills.fishing.lastChecked)}` : 'Not tracked yet'}
                        />
                        <StatTile
                            label="Gardening XP"
                            value={stats.skills.gardening ? formatXp(stats.skills.gardening.experience) : '-'}
                            sub={stats.skills.gardening ? `Last checked ${formatDate(stats.skills.gardening.lastChecked)}` : 'Not tracked yet'}
                        />
                    </StatCategory>

                    <StatCategory header="Content &amp; Moderation" tint="slategray">
                        <StatTile label="FranklyCloud Items" value={formatNumber(stats.content.cloudItems)} />
                        <StatTile label="Saved Imager Scenes" value={formatNumber(stats.content.imagerScenes)} />
                        <StatTile label="Furni Subscriptions" value={formatNumber(stats.content.furniSubscriptions)} />
                        <StatTile label="Profile Skins Owned" value={formatNumber(stats.content.profileSkins)} />
                        <StatTile label="Reports Filed" value={formatNumber(stats.moderation.reportsFiled)} />
                        <StatTile label="Reports Against" value={formatNumber(stats.moderation.reportsAgainst)} />
                    </StatCategory>

                    <ContentBox theme="default" tint="goldenrod" header="Recent Framp Activity">
                        {stats.stamps.recent.length === 0 ? (
                            <p className="text-sm uppercase text-gray-500">No framp activity yet</p>
                        ) : (
                            <div className="space-y-2">
                                {stats.stamps.recent.map((entry) => (
                                    <div key={entry.logId} className="bg-gray-100 p-2 rounded flex items-center gap-3 text-sm">
                                        <span className={`font-bold w-16 shrink-0 ${entry.amount < 0 ? 'text-red-600' : 'text-green-700'}`}>
                                            {entry.amount > 0 ? '+' : ''}{formatNumber(entry.amount)}
                                        </span>
                                        <span className="flex-1 min-w-0 truncate">{entry.reason}</span>
                                        <span className="text-xs text-gray-500 shrink-0">{formatDateTime(entry.createdAt)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ContentBox>
                </>
            )}
        </AdminLayout>
    );
}

export default UserLookup;
